// Datenzustand des Analysen-Blocks („Allgemeine Analyse“ + „Detailanalysen“).
//
// Aus ContactRisksPanel herausgelöst, weil der Block seit dem Reiter „Analyse“
// an zwei Stellen gerendert wird. Der Hook kapselt die drei kontakt-scoped GETs
// und die 3-Zustands-Maschine der allgemeinen Analyse, damit beide Render-Orte
// dieselbe Wahrheit sehen, ohne dass eine Prop-Kaskade nötig wird.

import { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl } from "../../utils/api";
import type { RiskObjectLite } from "./risikoobjektLabel";
import type {
  ExistingKundenLink,
  RisikoanalyseStartMode,
} from "./RisikoanalyseStartPopup";

export type PublicFormStatus = {
  id: number;
  token: string;
  formType: string;
  url: string;
  expiresAt: string | null;
  status: "NOT_FILLED" | "FILLED";
  submitted: boolean;
  canApply: boolean;
  /** Latest submission version string (e.g. "kurz-step-form-v1", "berater-step-form-v1"). */
  version?: string | null;
};

export type AnalyseRiskObject = RiskObjectLite & {
  id: number;
  contactID?: number;
  contractID?: number | null;
  contract?: { id: number; versicherungsnummer?: string } | null;
};

export type AnalysenState = {
  /** Risikoobjekte ohne die Platzhalter „wartet auf Detailerfassung“. */
  realRisks: AnalyseRiskObject[];
  /** 3-Zustands-Maschine der allgemeinen Analyse. */
  popupMode: RisikoanalyseStartMode;
  /** Token der abgeschickten Kundenmodus-Erfassung (sonst null). */
  submittedToken: string | null;
  /** Noch offener, verschickter Kundenlink (sonst null). */
  existingKundenLink: ExistingKundenLink | null;
  /** True, sobald die allgemeine Analyse in irgendeinem Modus ausgefüllt ist. */
  generalAusgefuellt: boolean;
  reload: () => void;
};

/**
 * @param externalRisks Bereits geladene Risikoobjekte des Aufrufers. Gesetzt vom
 *   Risiken-Reiter, der sie ohnehin hat: sonst liefe derselbe GET zweimal und
 *   beide Hälften des Reiters hingen an unterschiedlichen Momentaufnahmen.
 *   Der Analyse-Reiter lässt den Parameter weg und lädt selbst.
 */
export function useAnalysenState(
  contactId: number | undefined,
  externalRisks?: AnalyseRiskObject[],
): AnalysenState {
  const [risks, setRisks] = useState<AnalyseRiskObject[]>([]);
  const laedtRisksSelbst = externalRisks === undefined;
  const [forms, setForms] = useState<PublicFormStatus[]>([]);
  // Das Beratermodus-Signal kommt direkt vom Kontakt (jsonb-Spalten), nicht aus
  // einem PublicFormToken — deshalb ein eigener Call.
  const [beratermodusDraft, setBeratermodusDraft] = useState<{
    submittedAt: string | null;
    version: string | null;
  }>({ submittedAt: null, version: null });

  const reload = useCallback(async () => {
    if (contactId == null) return;
    try {
      const [risksRes, formsRes, internalRes] = await Promise.all([
        laedtRisksSelbst
          ? fetch(apiUrl(`risk-objects/by-contact/${contactId}`))
          : Promise.resolve(null),
        fetch(apiUrl(`contact/${contactId}/public-forms`)),
        fetch(apiUrl(`contact/${contactId}/risikoanalyse/internal-state`)),
      ]);
      if (risksRes?.ok) {
        const data = await risksRes.json();
        setRisks(Array.isArray(data) ? (data as AnalyseRiskObject[]) : []);
      }
      if (formsRes.ok) {
        const data = await formsRes.json();
        setForms(Array.isArray(data) ? (data as PublicFormStatus[]) : []);
      }
      if (internalRes.ok) {
        const data = await internalRes.json();
        setBeratermodusDraft({
          submittedAt: data?.draftSubmittedAt ?? null,
          version: data?.draftVersion ?? null,
        });
      }
    } catch {
      /* ignore — die Kacheln zeigen dann den Nicht-ausgefüllt-Zustand */
    }
  }, [contactId, laedtRisksSelbst]);

  useEffect(() => {
    reload();
  }, [reload]);

  const realRisks = useMemo(
    () =>
      (externalRisks ?? risks).filter(
        (r) => !(typeof r.name === "string" && r.name.includes("wartet auf Detailerfassung")),
      ),
    [externalRisks, risks],
  );

  /* Allgemeine Analyse — 3 Zustände. Beratermodus-Signal aus dem Kontakt
   * (draftSubmittedAt != null), Kundenmodus weiterhin aus den
   * kurz/kurz-scroll-PublicFormTokens. */
  const { popupMode, submittedToken } = useMemo<{
    popupMode: RisikoanalyseStartMode;
    submittedToken: string | null;
  }>(() => {
    if (beratermodusDraft.submittedAt) {
      return { popupMode: "beratermodus", submittedToken: null };
    }
    const kurz = forms.find(
      (f) =>
        (f.formType === "risikoanalyse-kurz" || f.formType === "risikoanalyse-kurz-scroll") &&
        f.submitted,
    );
    if (kurz) return { popupMode: "kundenmodus", submittedToken: kurz.token };
    return { popupMode: "none", submittedToken: null };
  }, [forms, beratermodusDraft]);

  /** Zuletzt verschickter, noch nicht abgeschickter Kundenlink. */
  const existingKundenLink: ExistingKundenLink | null = useMemo(() => {
    const pending = forms.find(
      (f) =>
        (f.formType === "risikoanalyse-kurz" || f.formType === "risikoanalyse-kurz-scroll") &&
        !f.submitted,
    );
    if (!pending) return null;
    return { url: pending.url, token: pending.token };
  }, [forms]);

  return {
    realRisks,
    popupMode,
    submittedToken,
    existingKundenLink,
    generalAusgefuellt: popupMode !== "none",
    reload,
  };
}
